import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../redux/store';
import { useUserLanguage } from '../services/userLanguageService';
import { userSelectOneObjectByQuery } from '../redux/thunks/DBServiceThunks';

/**
 * Hook to initialize user language preference on app load
 * Priority: User's database language > localStorage > browser language > 'en'
 */
export const useInitializeUserLanguage = () => {
  const { i18n } = useTranslation();
  const { getUserLanguage } = useUserLanguage();
  const user = useAppSelector(state => state.UserReducer.user);
  const isAuthenticated = useAppSelector(state => state.UserReducer.isAuthenticated);
  const initialized = useAppSelector(state => state.UserReducer.initialized);
  const dbUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, { _id: user?._id }));
  const hasInitialized = useRef(false);
  const lastProcessedUserId = useRef<string | null>(null);

  // Reset initialization flag when user changes
  if (user?._id !== lastProcessedUserId.current) {
    hasInitialized.current = false;
    lastProcessedUserId.current = user?._id || null;
  }


  useEffect(() => {
    const initializeLanguage = async () => {

      // Only run ONCE when user first authenticates - but don't block on hasInitialized if not authenticated yet
      if (!isAuthenticated || !initialized || !user) {
        return;
      }

      // If user is authenticated but we already initialized for this user, skip
      if (hasInitialized.current) {
        return;
      }

      // Wait for database user to be loaded before initializing language
      if (!dbUser) {
        return;
      }

      try {
        // Get user's saved language preference
        const userLanguage = getUserLanguage();
        
        // If user has a saved language preference and it's different from current
        if (userLanguage && userLanguage !== i18n.language) {
          await i18n.changeLanguage(userLanguage);
        } else if (!userLanguage) {
          // If no saved preference, use current i18n language (from localStorage/browser/fallback)
        }
      } catch (error) {
        // Fallback to default language
        if (i18n.language !== 'en') {
          await i18n.changeLanguage('en');
        }
      } finally {
        // Mark as initialized to prevent future runs FOR THIS USER
        hasInitialized.current = true;
      }
    };

    initializeLanguage();
  }, [isAuthenticated, initialized, user, dbUser, getUserLanguage, i18n]); // Added dbUser dependency

  return null; // This is a side-effect only hook
}; 